import type {
  CountableApiResponse,
  CreateCreditInvoiceDto,
  Customer,
  SerializedCreditInvoice,
  UpdateCreditInvoiceDto,
} from '@enmon/commons/lib/enmon';
import { BASE_REST_API_URL } from 'common/constants';
import type { EssentialResellersDataItem } from 'components/form/select/queries/getEssentialResellersData';
import type { StrictOmit } from '@enmon/commons';
import { BaseApiClient } from './baseApiClient';

type CustomerDataItem = Pick<Customer, '_id' | 'name'>;

export type GetCreditInvoicesArg = CreditInvoiceFilter & {
  populate?: (keyof SerializedCreditInvoice)[];
};

export type CreateCreditInvoiceArg = CreateCreditInvoiceDto;

export type UpdateCreditInvoiceArg = { _id: SerializedCreditInvoice['_id']; data: UpdateCreditInvoiceDto };

export type DeleteCreditInvoiceArg = SerializedCreditInvoice['_id'];
export type PopulatedSerializedCreditInvoice = StrictOmit<SerializedCreditInvoice, 'customer' | 'reseller'> & {
  customer: CustomerDataItem;
  reseller: EssentialResellersDataItem;
};

export type GetCreditInvoicesResponse = CountableApiResponse<PopulatedSerializedCreditInvoice[]>;
export type UpdateCreditInvoiceResponse = SerializedCreditInvoice;
export type CreateCreditInvoiceResponse = SerializedCreditInvoice;
export type DeleteCreditInvoiceResponse = void;

export type CreditInvoiceFilter = {
  sort?: string;
  page?: number;
  pageSize?: number;
  resellers?: string[];
  customers?: string[];
  search?: string;
};

/**
 * @deprecated Use class instead because there is only one implementation of this interface which makes searching for references
 * more difficult, name shouldn't be prefixed too: https://blog.nette.org/en/prefixes-and-suffixes-do-not-belong-in-interface-names.
 */
export interface IInvoiceCreditsApiClient {
  find: (filter: GetCreditInvoicesArg) => Promise<GetCreditInvoicesResponse>;
  create: (args: CreateCreditInvoiceArg) => Promise<CreateCreditInvoiceResponse>;
  update: (args: UpdateCreditInvoiceArg) => Promise<UpdateCreditInvoiceResponse>;
  delete: (id: DeleteCreditInvoiceArg) => Promise<DeleteCreditInvoiceResponse>;
}

export class CreditInvoicesApiClient extends BaseApiClient implements IInvoiceCreditsApiClient {
  constructor(jwt: string) {
    super('enmon-tools/credit-invoices', BASE_REST_API_URL, jwt, {
      arrayFormat: 'comma',
    });
  }

  public async find(filter: GetCreditInvoicesArg): Promise<GetCreditInvoicesResponse> {
    const response = await this.axios.get<GetCreditInvoicesResponse>('', {
      params: filter,
    });

    return response.data;
  }

  public async create(data: CreateCreditInvoiceArg): Promise<CreateCreditInvoiceResponse> {
    const response = await this.axios.post<CreateCreditInvoiceResponse>('', data);

    return response.data;
  }

  public async update({ _id, data }: UpdateCreditInvoiceArg): Promise<UpdateCreditInvoiceResponse> {
    const response = await this.axios.patch<UpdateCreditInvoiceResponse>(`${_id}`, data);

    return response.data;
  }

  public async delete(id: DeleteCreditInvoiceArg): Promise<DeleteCreditInvoiceResponse> {
    const response = await this.axios.delete<DeleteCreditInvoiceResponse>(`${id}`);

    return response.data;
  }
}
